import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '~/UIKit/Style';
import { USCConnection, ZoomWebinarHintIcon, Zoom } from '~/Components/Icons';
import { IItemCreationSubsectionInputProps, ItemCreationSubsection } from '~/Components';
import { ExtendedEventGroup } from '../ExtendedEventGroup.model';
import { ExtendedEvent } from '../ExtendedEvent.model';
import { EditEventGroupButton } from './EditEventGroupButton';
import { useProvider } from '~/Hooks';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { BookingDetails } from '~/Components/Icons/BookingDetails';
import { UpgradeBanner } from './UpgradeBanner';
import { routeReplacer } from '~/Helpers/String.helper';
import { useRequest } from '~/Hooks/useRequest';
import { CoreProviderModel } from '~/Models/DTO/CoreProvider.model';
import { CORE_SERVICE_URLS } from '~/Constants/Urls.constants';
import { useCancellationWindowInputs } from './hooks/useCancellationInputs';

const useStyles = makeStyles(() => ({
  classIcon: {
    height: '32px',
    width: '32px',
  },
}));
export const SettingsSection = ({
  eventGroup,
  event,
}: {
  eventGroup: ExtendedEventGroup;
  event?: ExtendedEvent;
}) => {
  const { t } = useTranslation();
  const { isUsingThePartnerTool } = useProvider();
  const { classes } = useStyles();
  const boundariesElement = useRef(null);
  const { currentProvider } = useProvider();
  const { requestWithCallback } = useRequest();
  const [coreProviderSubscription, setcoreProviderSubscription] = useState('');

  const freePackage =
    coreProviderSubscription === 'Free_Package' ||
    coreProviderSubscription === 'pro4free' ||
    coreProviderSubscription === null;
  const TOOLTIP_PLACEMENT = 'bottom-start';

  const {
    freeCancellationInputs,
    paidCancellationInputs,
    allCancellationInputs,
  } = useCancellationWindowInputs({
    event,
    eventGroup,
    notEventGroupOrFreePackage: !!event || freePackage,
  });

  //TODO Temporary call to cores provider subscription type, remove when subscription service is live
  useEffect(() => {
    const getCoreProviderSubscription = () => {
      requestWithCallback<CoreProviderModel>(
        {
          url: routeReplacer(CORE_SERVICE_URLS.CORE_PROVIDER, {
            domain: currentProvider?.domain || '',
          }),
          method: 'GET',
        },
        (data) => {
          setcoreProviderSubscription(data.stripeSubscriptionPlanId!);
        },
      );
    };
    getCoreProviderSubscription();
  }, [currentProvider]);

  const SettingsSectionInputs: IItemCreationSubsectionInputProps<Partial<ExtendedEventGroup>>[] = [
    {
      icon: <BookingDetails />,
      label: isUsingThePartnerTool
        ? t('event.creation.settings.bookingDetails')
        : t('settings.bookingGroup'),
      placeholder: t('event.creation.settings.bookingDetails'),
      type: ItemCreationValidInput.BOOKINGGROUP_SELECTOR,
      name: 'bookingGroupIds',
      disabled: !!event,
    },
  ];

  const ThirdPartySectionInputs: IItemCreationSubsectionInputProps<
    Partial<ExtendedEventGroup>
  >[] = [
    {
      icon: <USCConnection className={classes.classIcon} />,
      label: t('event.creation.settings.showInApp'),
      type: ItemCreationValidInput.SWITCHER,
      name: 'isUSCIntegrationEnabled',
      noIconBackground: true,
      disabled: !!event,
    },
  ];

  const zoomSection: IItemCreationSubsectionInputProps<ExtendedEvent>[] = [];
  if (eventGroup.hasAccessToZoom) {
    if (!eventGroup.livestream) {
      eventGroup.isZoomSelected = false;
      eventGroup.isZoomWebinar = false;
    }
    if (eventGroup.livestream) {
      zoomSection.push({
        icon: <Zoom className={classes.classIcon} />,
        label: t('form.field.zoom'),
        type: ItemCreationValidInput.SWITCHER,
        name: 'isZoomSelected',
      });
    }
    if (eventGroup.isZoomSelected) {
      zoomSection.push({
        icon: <div />,
        noIconBackground: true,
        label: t('form.field.type'),
        placeholder: t('form.placeholder.meeting'),
        secondaryPlaceholder: t('form.placeholder.webinar'),
        type: ItemCreationValidInput.SWITCHER,
        name: 'isZoomWebinar',
        InputProps: { style: { width: 228 } },
      });
    }
  }

  return (
    <>
      {!!event && <EditEventGroupButton eventGroup={eventGroup} eventType={event.type} />}
      {!isUsingThePartnerTool && (
        <>
          <ItemCreationSubsection
            title={t('event.creation.settings.settingsInputs')}
            isHeaderVisible={false}
            items={SettingsSectionInputs}
          />
          <ItemCreationSubsection
            title={t('event.creation.settings.thirdPartyConnection')}
            isHeaderVisible
            items={ThirdPartySectionInputs}
          />
        </>
      )}
      {!!event && (
        <ItemCreationSubsection
          title={t('form.title.locationContacts')}
          tooltip={
            eventGroup.hasAccessToZoom && {
              title: t('event.zoomWebinarHint', {
                zoomLink: 'https://zoom.us',
                zoomSupportLink: t('event.zoomLink'),
              }),
              icon: <ZoomWebinarHintIcon />,
              boundariesElement: boundariesElement.current,
            }
          }
          isHeaderVisible
          items={zoomSection}
        />
      )}

      {!freePackage || isUsingThePartnerTool ? (
        <ItemCreationSubsection
          title={isUsingThePartnerTool ? t('event.creation.settings.cancellation') : undefined}
          isHeaderVisible
          items={allCancellationInputs}
          tooltip={
            isUsingThePartnerTool
              ? {
                  title: t('partner.event.create.tooltip.cancellation'),
                  placement: TOOLTIP_PLACEMENT,
                }
              : eventGroup.isUSCIntegrationEnabled && {
                  title: t('event.create.tooltip.cancellation'),
                  placement: TOOLTIP_PLACEMENT,
                }
          }
        />
      ) : (
        <>
          <ItemCreationSubsection
            isHeaderVisible
            items={freeCancellationInputs}
            tooltip={
              eventGroup.isUSCIntegrationEnabled && {
                title: t('event.create.tooltip.cancellation'),
                placement: TOOLTIP_PLACEMENT,
              }
            }
          />
          {<UpgradeBanner />}
          <ItemCreationSubsection
            title={t('event.creation.settings.cancellation')}
            isHeaderVisible={false}
            items={paidCancellationInputs}
          />
        </>
      )}
    </>
  );
};
